import { useMemo, useState } from "react";
import { newsItems, categories, type NewsItem } from "@/mocks/gossip";
import ArticleModal from "./ArticleModal";

const catColor: Record<string, string> = {
  fresh: "bg-primary-100 text-primary-700 border-primary-200",
  deny: "bg-secondary-100 text-secondary-700 border-secondary-200",
  respond: "bg-accent-100 text-accent-800 border-accent-200",
  progress: "bg-background-200 text-foreground-700 border-background-300",
};

export default function FeedSection() {
  const [active, setActive] = useState("all");
  const [selected, setSelected] = useState<NewsItem | null>(null);

  const list = useMemo(
    () => (active === "all" ? newsItems : newsItems.filter((n) => n.cat === active)),
    [active],
  );

  const featured = newsItems.find((n) => n.hot) ?? newsItems[0];

  return (
    <section id="hot" className="scroll-mt-20 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-primary-600">
              <i className="ri-fire-line"></i>
              <span>全网滚动热瓜</span>
            </div>
            <h2 className="font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
              最新热瓜 · 实时更新
            </h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600 md:text-base">
              按栏目一键筛选，点开任意卡片即可查看完整事件脉络与后续追踪。
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActive("all")}
              className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                active === "all"
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-200/70 text-foreground-700 hover:bg-background-300/70"
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.key}
                onClick={() => setActive(c.key)}
                className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  active === c.key
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-200/70 text-foreground-700 hover:bg-background-300/70"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {/* 头条大卡 */}
        <div
          onClick={() => setSelected(featured)}
          className="group mt-8 grid cursor-pointer overflow-hidden rounded-xl border border-background-200 bg-background-100 lg:grid-cols-2"
        >
          <div className="relative h-64 w-full overflow-hidden lg:h-auto">
            <img
              src={featured.image}
              alt={featured.imgAlt}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-bold text-background-50">
              <i className="ri-flashlight-fill"></i>
              头条
            </span>
          </div>
          <div className="flex flex-col justify-center gap-4 p-6 md:p-8">
            <div className="flex items-center gap-2 text-xs text-foreground-600">
              <span className={`inline-flex items-center rounded border px-2 py-0.5 font-semibold ${catColor[featured.cat]}`}>
                {featured.catName}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {featured.time}
              </span>
            </div>
            <h3 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              {featured.title}
            </h3>
            <p className="text-sm leading-relaxed text-foreground-600 md:text-base">{featured.excerpt}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {featured.read}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>
                  {featured.comment}
                </span>
                <span className="hidden items-center gap-1 sm:flex">
                  <i className="ri-price-tag-3-line"></i>
                  {featured.tag}
                </span>
              </div>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 group-hover:gap-2 transition-all">
                查看详情
                <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </div>
        </div>

        {/* 列表卡片 */}
        <div key={active} className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((item) => (
            <article
              key={item.id}
              onClick={() => setSelected(item)}
              className="group flex cursor-pointer flex-col overflow-hidden rounded-xl border border-background-200 bg-background-100 transition-colors hover:border-background-300"
            >
              <div className="relative h-44 w-full overflow-hidden sm:h-48">
                <img
                  src={item.image}
                  alt={item.imgAlt}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className={`absolute left-3 top-3 inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold ${catColor[item.cat]}`}>
                  {item.catName}
                </span>
                {item.hot && (
                  <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-md bg-primary-500/95 px-2 py-0.5 text-xs font-bold text-background-50">
                    <i className="ri-fire-fill"></i>热
                  </span>
                )}
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                  {item.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{item.excerpt}</p>
                <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {item.time}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {item.read}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line"></i>
                      {item.comment}
                    </span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {selected && <ArticleModal item={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}